/**
 * Chord AI API
 * - Chord service (via gateway /api/chords): upload-url, analyze, get analysis.
 * - Org service (via gateway /api/organizations/churches/{churchId}/chord-uploads):
 *   church-scoped chord upload records.
 *
 * Upload flow: getUploadUrl -> PUT file to blob (SAS) -> create org record ->
 * analyze -> poll analysis -> persist final JSON back to the org record.
 */

import apiClient from './client';

export interface ChordSegment {
  time: number;
  chord: string;
  duration: number;
}

export type ChordUploadStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface ChordUpload {
  id?: string;
  churchId?: string;
  sourceId: string;
  sourceType?: string;
  blobName?: string;
  contentHash?: string;
  title: string;
  artist?: string;
  keyHint?: string;
  youtubeUrl?: string;
  includeStems?: boolean;
  status: ChordUploadStatus;
  detectedKey?: string;
  bpm?: number;
  chords?: ChordSegment[];
  error?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Set when a downgrade put this record into the retention grace window (kept but scheduled for deletion). */
  lockedAt?: string;
  /** When this record and its audio/stems are permanently deleted unless the church re-subscribes. */
  purgeAt?: string;
}

export interface UploadUrlResponse {
  sourceId: string;
  blobName: string;
  uploadUrl: string;
  expiresAt: string;
  method: string;
  requiredHeaders: Record<string, string>;
  /** SHA-256 the server used to derive a content-addressed sourceId (dedup). */
  contentHash?: string;
  /** True when this exact content is already uploaded; the PUT can be skipped. */
  duplicate?: boolean;
}

export interface ChordAnalysis {
  videoId: string;
  sourceId: string;
  chords: ChordSegment[];
  status: ChordUploadStatus;
  detectedKey?: string;
  bpm?: number;
  includeStems?: boolean;
  stemUrls?: Record<string, string>;
  error?: string;
}

/**
 * SHA-256 of a file's bytes as lowercase hex, used as a content fingerprint so
 * re-uploading the same song reuses one blob + analysis instead of polluting
 * storage and the church library with duplicates. Uses the Web Crypto API.
 */
export async function sha256Hex(file: Blob): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ----------------------------------------------------------------------------
// Chord analysis service (Demucs + chord detection worker)
// ----------------------------------------------------------------------------

export const chordsApi = {
  /** Request a short-lived write SAS to upload audio directly to blob storage.
   * Pass the file's SHA-256 so the server derives a content-addressed sourceId
   * and can report `duplicate` when the same content was already uploaded. */
  getUploadUrl: async (fileName: string, contentHash?: string): Promise<UploadUrlResponse> => {
    const res = await apiClient.post<UploadUrlResponse>('/api/chords/upload-url', { fileName, contentHash });
    return res.data;
  },

  /**
   * Upload the raw file bytes. Azure returns an absolute SAS URL (direct PUT,
   * no auth); local dev returns a relative worker path that we PUT through the
   * gateway api client so auth + CORS are handled like every other call.
   *
   * Azure uploads are RESILIENT: large files are staged as 8 MB blocks
   * (Put Block + Put Block List) and every request retries with backoff, so a
   * transient network drop re-sends one block instead of failing the whole
   * upload (large single PUTs were dying mid-transfer as storage NetworkErrors).
   */
  uploadToBlob: async (uploadUrl: string, file: File, requiredHeaders: Record<string, string>): Promise<void> => {
    const contentType = file.type || 'application/octet-stream';
    const isAbsolute = /^https?:\/\//i.test(uploadUrl);
    if (isAbsolute) {
      const BLOCK_SIZE = 8 * 1024 * 1024;
      const MAX_ATTEMPTS = 4;

      const putWithRetry = async (url: string, init: RequestInit): Promise<void> => {
        let lastError: unknown;
        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
          try {
            const response = await fetch(url, { ...init, method: 'PUT' });
            if (response.ok) return;
            // 5xx/408 are transient (worth retrying); other 4xx are permanent
            // (e.g. 403 expired SAS) so fail fast.
            if (response.status < 500 && response.status !== 408) {
              throw new Error(`Blob upload failed (${response.status})`);
            }
            lastError = new Error(`Blob upload failed (${response.status})`);
          } catch (err) {
            if (err instanceof Error && /^Blob upload failed \(4/.test(err.message)) throw err;
            lastError = err; // network error / connection closed mid-transfer
          }
          if (attempt < MAX_ATTEMPTS - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500 * 3 ** attempt));
          }
        }
        throw lastError instanceof Error
          ? lastError
          : new Error('Upload connection was interrupted. Please try again.');
      };

      // Small files: one retried PUT.
      if (file.size <= BLOCK_SIZE) {
        await putWithRetry(uploadUrl, {
          headers: { ...requiredHeaders, 'Content-Type': contentType },
          body: file,
        });
        return;
      }

      // Large files: staged block upload, one retried PUT per 8 MB block.
      const sep = uploadUrl.includes('?') ? '&' : '?';
      const blockIds: string[] = [];
      for (let offset = 0, index = 0; offset < file.size; offset += BLOCK_SIZE, index++) {
        const blockId = btoa(String(index).padStart(12, '0'));
        blockIds.push(blockId);
        await putWithRetry(`${uploadUrl}${sep}comp=block&blockid=${encodeURIComponent(blockId)}`, {
          body: file.slice(offset, offset + BLOCK_SIZE),
        });
      }
      const blockList = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds
        .map((id) => `<Latest>${id}</Latest>`)
        .join('')}</BlockList>`;
      await putWithRetry(`${uploadUrl}${sep}comp=blocklist`, {
        headers: { 'x-ms-blob-content-type': contentType, 'Content-Type': 'application/xml' },
        body: blockList,
      });
      return;
    }
    await apiClient.put(uploadUrl, file, {
      headers: { ...requiredHeaders, 'Content-Type': contentType },
    });
  },

  /** Kick off chord analysis for an uploaded blob (or a YouTube videoId). */
  analyze: async (body: {
    uploadBlobName?: string;
    videoId?: string;
    keyHint?: string;
    includeStems?: boolean;
  }): Promise<ChordAnalysis> => {
    const res = await apiClient.post<ChordAnalysis>('/api/chords/analyze', body);
    return res.data;
  },

  /** Fetch the current analysis (chords + stem URLs) by sourceId. */
  getAnalysis: async (sourceId: string): Promise<ChordAnalysis> => {
    const res = await apiClient.get<ChordAnalysis>(`/api/chords/${sourceId}`);
    return res.data;
  },

  /** Delete the stored audio + separated stems for a source (used when removing a song). */
  deleteSource: async (sourceId: string): Promise<void> => {
    await apiClient.delete(`/api/chords/${encodeURIComponent(sourceId)}`);
  },
};

// ----------------------------------------------------------------------------
// Church-scoped chord uploads (organization service)
// ----------------------------------------------------------------------------

const base = (churchId: string) => `/api/organizations/churches/${churchId}/chord-uploads`;

export const CHORD_LIBRARY_SONG_LIMIT = 20;

export interface CreateChordUploadRequest extends Partial<ChordUpload> {
  replaceSourceId?: string;
}

export const chordUploadsApi = {
  list: async (churchId: string): Promise<ChordUpload[]> => {
    const res = await apiClient.get<ChordUpload[]>(base(churchId));
    return res.data;
  },

  get: async (churchId: string, sourceId: string): Promise<ChordUpload> => {
    const res = await apiClient.get<ChordUpload>(`${base(churchId)}/${sourceId}`);
    return res.data;
  },

  create: async (churchId: string, dto: CreateChordUploadRequest): Promise<ChordUpload> => {
    const res = await apiClient.post<ChordUpload>(base(churchId), dto);
    return res.data;
  },

  update: async (churchId: string, sourceId: string, dto: Partial<ChordUpload>): Promise<ChordUpload> => {
    const res = await apiClient.put<ChordUpload>(`${base(churchId)}/${sourceId}`, dto);
    return res.data;
  },

  remove: async (churchId: string, sourceId: string): Promise<void> => {
    await apiClient.delete(`${base(churchId)}/${sourceId}`);
  },
};
